import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, IconButton, InputAdornment, MenuItem, FormControl, InputLabel, Select, Box, FormHelperText
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const EditUserDialog = ({ open, onClose, userProfile, onSave, mode = "edit", roles = {} }) => {
  const { t } = useTranslation();
  const isCreate = mode === "create";

  const [form, setForm] = useState({
    username: "", first_name: "", last_name: "", email: "", dataview_pin: "", password: "", role: ""
  });
  const [errors, setErrors] = useState({ username: "", password: "", role: "" });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (isCreate || !userProfile) {
      setForm({
        username: "", first_name: "", last_name: "", email: "", dataview_pin: "", password: "",
        role: Object.keys(roles)?.[0] || "",
      });
      setErrors({ username: "", password: "", role: "" });
      return;
    }
    setForm({
      username: userProfile?.user?.username || "",
      first_name: userProfile?.user?.first_name || "",
      last_name: userProfile?.user?.last_name || "",
      email: userProfile?.user?.email || "",
      dataview_pin: userProfile?.dataview_pin || "",
      password: "",
      role: userProfile?.role || "",
    });
    setErrors({ username: "", password: "", role: "" });
  }, [userProfile, isCreate, roles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));

    if (isCreate) {
      if (name === "username") setErrors((p) => ({ ...p, username: value.trim() ? "" : t("editUserDialog.validation.usernameRequired", "Username is required") }));
      if (name === "password") setErrors((p) => ({ ...p, password: value ? "" : t("editUserDialog.validation.passwordRequired", "Password is required") }));
      if (name === "role") setErrors((p) => ({ ...p, role: value ? "" : t("editUserDialog.validation.roleRequired", "Role is required") }));
    }
  };

  const validateCreate = () => {
    if (!isCreate) return true;
    const nextErrors = {
      username: form.username.trim() ? "" : t("editUserDialog.validation.usernameRequired", "Username is required"),
      password: form.password ? "" : t("editUserDialog.validation.passwordRequired", "Password is required"),
      role: form.role ? "" : t("editUserDialog.validation.roleRequired", "Role is required"),
    };
    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleSave = async () => {
    if (isCreate && !validateCreate()) return;

    setSaving(true);
    try {
      const basePayload = {
        username: form.username,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        dataview_pin: form.dataview_pin,
        ...(form.password ? { password: form.password } : {}),
      };

      const payload = isCreate ? { ...basePayload, role: form.role, password: form.password } : basePayload;
      await onSave(userProfile?.id, payload, mode);
    } finally {
      setSaving(false);
    }
  };

  const createDisabled =
    isCreate && (!form.role || !form.username.trim() || !form.password || !!errors.username || !!errors.password || !!errors.role);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isCreate ? t("editUserDialog.titleCreate", "Create User") : t("editUserDialog.titleEdit", "Edit User")}</DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          required={isCreate}
          error={!!errors.username}
          helperText={isCreate ? (errors.username || " ") : " "}
          label={t("editUserDialog.fields.username", "Username")}
          name="username"
          value={form.username}
          onChange={handleChange}
          margin="normal"
        />

        <TextField
          fullWidth
          required={isCreate}
          error={!!errors.password}
          label={isCreate ? t("editUserDialog.fields.password", "Password") : t("editUserDialog.fields.newPassword", "New password (to change)")}
          name="password"
          type={showPw ? "text" : "password"}
          value={form.password}
          onChange={handleChange}
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPw((s) => !s)}
                  edge="end"
                  aria-label={showPw ? t("editUserDialog.actions.hidePassword", "Hide password") : t("editUserDialog.actions.showPassword", "Show password")}
                >
                  {showPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={
            isCreate
              ? (errors.password || t("editUserDialog.validation.passwordRequired", "Password is required"))
              : t("editUserDialog.help.passwordLeaveBlank", "Leave blank to keep current password")
          }
        />

        <Box display="flex" gap={2}>
          <TextField fullWidth label={t("editUserDialog.fields.firstName", "First name")} name="first_name" value={form.first_name} onChange={handleChange} margin="normal" />
          <TextField fullWidth label={t("editUserDialog.fields.lastName", "Last name")} name="last_name" value={form.last_name} onChange={handleChange} margin="normal" />
        </Box>

        <TextField fullWidth label={t("editUserDialog.fields.email", "Email")} name="email" type="email" value={form.email} onChange={handleChange} margin="normal" />

        {isCreate && (
          <FormControl fullWidth margin="normal" required error={!!errors.role}>
            <InputLabel id="role-label">{t("editUserDialog.fields.role", "Role")}</InputLabel>
            <Select
              labelId="role-label"
              label={t("editUserDialog.fields.role", "Role")}
              name="role"
              value={form.role}
              onChange={handleChange}
            >
              {Object.keys(roles).length === 0 ? (
                <MenuItem value="" disabled>{t("editUserDialog.noRoles", "No roles available")}</MenuItem>
              ) : (
                Object.keys(roles).map((r) => (
                  <MenuItem key={r} value={r}>{roles[r].label}</MenuItem>
                ))
              )}
            </Select>
            {!!errors.role && <FormHelperText>{errors.role}</FormHelperText>}
          </FormControl>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          {t("editUserDialog.actions.cancel", "Cancel")}
        </Button>
        <Button variant="outlined" onClick={handleSave} disabled={saving || createDisabled}>
          {saving
            ? (isCreate ? t("editUserDialog.actions.creating", "Creating...") : t("editUserDialog.actions.saving", "Saving..."))
            : (isCreate ? t("editUserDialog.actions.create", "Create") : t("editUserDialog.actions.save", "Save"))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
